import { Divider, Stack, Tooltip, Typography } from '@mui/material';
import {
  type Role,
  AccessConditionEffect,
  type AccessCondition,
} from '@repo/api-gw-sdk';
import React from 'react';

import { OptionMenu } from '@/components/option-menu/option-menu';

import { RoleConditionConditionSupportedProperties } from './utils';

import { ConditionViewer } from '../conditions/ConditionViewer';

export const RoleConditionComponent = ({
  role,

  roleCondition,
  showActions,
  onEdit,
  onDelete,
}:
  | {
      role: Role;
      roleCondition: AccessCondition;
      showActions: true;
      onEdit: () => void;
      onDelete: () => void;
    }
  | {
      role: Role;
      roleCondition: AccessCondition;
      showActions?: false;
      onEdit?: undefined;
      onDelete?: undefined;
    }) => {
  return (
    <Stack
      sx={{
        border: showActions ? '1px solid var(--mui-palette-divider)' : 'none',
        borderRadius: '16px',
      }}
    >
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        className='my-[16px] mx-[24px]'
      >
        <Typography className='font-semibold'>{roleCondition.id}</Typography>
        {showActions && (
          <OptionMenu
            options={[
              {
                id: 'edit-data-access-rule',
                text: 'Edit',
                menuItemProps: {
                  onClick: onEdit,
                },
              },
              {
                id: 'delete-data-access-rule',
                text: role.permissionGrants.some(
                  (x) => x.accessConditionId === roleCondition.id
                ) ? (
                  <Tooltip
                    title={
                      "This can't be deleted because it's applied to a permission."
                    }
                  >
                    <Typography
                      color='var(--mui-palette-text-disabled)'
                      className='bg-transparent hover:bg-transparent'
                    >
                      {'Delete'}
                    </Typography>
                  </Tooltip>
                ) : (
                  'Delete'
                ),
                menuItemProps: {
                  sx: {
                    ...(role.permissionGrants.some(
                      (x) => x.accessConditionId === roleCondition.id
                    )
                      ? {
                          cursor: 'default',

                          '&:hover': {
                            backgroundColor: 'transparent',
                          },
                        }
                      : {}),
                  },
                  onClick: role.permissionGrants.some(
                    (x) => x.accessConditionId === roleCondition.id
                  )
                    ? undefined
                    : onDelete,
                },
              },
            ]}
          />
        )}
      </Stack>
      <Divider />
      <Stack className='m-[24px]' gap='24px' alignItems='flex-start'>
        <Stack direction='row' alignItems='center' gap='6px'>
          <Typography
            className='font-semibold'
            color={
              roleCondition.effect ===
              AccessConditionEffect.RULE_EFFECT_INCLUSIVE
                ? 'var(--mui-palette-success-main)'
                : 'var(--mui-palette-error-main)'
            }
          >
            {roleCondition.effect ===
            AccessConditionEffect.RULE_EFFECT_INCLUSIVE
              ? 'Allow'
              : 'Block'}
          </Typography>
          <Typography>{`access to resources that meet these conditions:`}</Typography>
        </Stack>
        {roleCondition.condition && (
          <ConditionViewer
            condition={roleCondition.condition}
            supportProperties={RoleConditionConditionSupportedProperties.flat()}
          />
        )}
      </Stack>
    </Stack>
  );
};

export const RoleConditionTooltip = ({
  role,
  roleCondition,
}: {
  role: Role;
  roleCondition: AccessCondition;
}) => (
  <Tooltip
    disableInteractive={false}
    slotProps={{
      tooltip: {
        sx: {
          padding: 0,
          color: 'var(--mui-palette-text-primary)',
          backgroundColor: 'var(--mui-palette-background-paper)',
          minWidth: '420px',
          maxWidth: '600px',
          boxShadow: 'var(--mui-customShadows-panel)',
        },
      },
    }}
    title={
      <RoleConditionComponent
        role={role}
        roleCondition={roleCondition}
        showActions={false}
      />
    }
  >
    <Typography
      color={'var(--mui-palette-custom-ORANGE-02)'}
      variant='subtitle1'
      className='truncate'
      sx={{
        maxWidth: '120px',
        marginTop: '8px',
        fontWeight: '600',
      }}
    >
      {roleCondition.id}
    </Typography>
  </Tooltip>
);
